import { useLayoutEffect, useRef } from 'react';
import { motion, useScroll, useSpring } from 'framer-motion';
import { gsap } from '@/animations/gsap';
import { useLang } from '@/i18n/LanguageContext';
import MaskLines from '@/components/MaskLines';

function Scene({ scene, index, total }) {
  const innerRef = useRef(null);
  const spotRef = useRef(null);

  const onMove = (e) => {
    const el = innerRef.current;
    if (el) {
      const r = e.currentTarget.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      gsap.to(el, { x: x * 26, y: y * 16, duration: 0.7, ease: 'power2.out' });
      if (spotRef.current) {
        spotRef.current.style.setProperty('--mx', `${e.clientX - r.left}px`);
        spotRef.current.style.setProperty('--my', `${e.clientY - r.top}px`);
      }
    }
  };

  const big = scene.size === 'big';
  return (
    <article
      data-testid={`situation-scene-${index + 1}`}
      onMouseMove={onMove}
      className="relative flex h-full w-screen shrink-0 flex-col justify-center overflow-hidden border-l hairline px-10 xl:px-20"
    >
      <div
        ref={spotRef}
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(460px circle at var(--mx, 60%) var(--my, 50%), rgba(31,232,157,0.10), transparent 65%)',
        }}
      />
      <div className="absolute left-10 right-10 top-24 flex justify-between font-mono-label text-white/50 xl:left-20 xl:right-20">
        <span>{scene.n}</span>
        <span aria-hidden="true">
          {scene.n} / {String(total).padStart(2, '0')}
        </span>
      </div>

      <div ref={innerRef} className="will-change-transform">
        <p className={`font-display ${big ? 'text-[6.6vw]' : 'text-[4.6vw]'} leading-[0.88]`}>
          {scene.lines.map((l, i) => (
            <span className="block" key={i}>
              {l}
            </span>
          ))}
          {scene.extra &&
            scene.extra.map((l, i) => (
              <span className="block text-[3.6vw] text-stroke" key={`x${i}`}>
                {l}
              </span>
            ))}
        </p>
        <p className="mt-10 flex flex-wrap gap-x-8 gap-y-2 font-mono-label text-white/55">
          {scene.tags.map((tag) => (
            <span key={tag}>{tag}</span>
          ))}
        </p>
      </div>
    </article>
  );
}

export default function Situations() {
  const { t } = useLang();
  const s = t.situations;
  const wrapRef = useRef(null);
  const trackRef = useRef(null);
  const mWrapRef = useRef(null);
  const mTrackRef = useRef(null);

  useLayoutEffect(() => {
    const mm = gsap.matchMedia();
    mm.add('(min-width: 1024px) and (prefers-reduced-motion: no-preference)', () => {
      if (!wrapRef.current || !trackRef.current) return;
      const getDist = () => trackRef.current.scrollWidth - window.innerWidth;
      const tween = gsap.to(trackRef.current, {
        x: () => -getDist(),
        ease: 'none',
        scrollTrigger: {
          trigger: wrapRef.current,
          start: 'top top',
          end: () => '+=' + getDist(),
          scrub: 1,
          pin: true,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      });
      return () => {
        tween.scrollTrigger && tween.scrollTrigger.kill();
        tween.kill();
      };
    });
    // tablet/mobile: stesso principio, la sezione si ferma e lo scroll verticale fa scorrere le scene
    mm.add('(max-width: 1023px) and (prefers-reduced-motion: no-preference)', () => {
      const wrap = mWrapRef.current;
      const track = mTrackRef.current;
      if (!wrap || !track) return;
      track.style.overflowX = 'visible';
      const getDist = () => track.scrollWidth - track.clientWidth;
      const tween = gsap.to(track, {
        x: () => -getDist(),
        ease: 'none',
        scrollTrigger: {
          trigger: wrap,
          start: 'center center',
          end: () => '+=' + getDist(),
          scrub: 1,
          pin: true,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      });
      return () => {
        tween.scrollTrigger && tween.scrollTrigger.kill();
        tween.kill();
        track.style.overflowX = '';
      };
    });
    return () => mm.revert();
  }, [t]);

  const { scrollYProgress } = useScroll({ target: wrapRef, offset: ['start start', 'end end'] });
  const progress = useSpring(scrollYProgress, { stiffness: 90, damping: 25, mass: 0.4 });

  return (
    <section id="when" data-testid="section-situations" className="relative">
      {/* header */}
      <div className="px-5 pt-32 pb-14 md:px-10 md:pt-44 md:pb-20">
        <p className="font-mono-label text-[var(--pz-green)]">{s.kicker}</p>
        <h2 className="font-display mt-6 text-[13vw] md:text-[8.5vw]">
          <MaskLines lines={[s.title]} />
        </h2>
        <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
          <p className="font-mono-label text-white/55">{s.sub}</p>
          <p className="font-mono-label text-white/40">
            {s.hint} →
          </p>
        </div>
      </div>

      {/* desktop: pinned horizontal corridor */}
      <div ref={wrapRef} className="relative hidden h-screen overflow-hidden border-y hairline lg:block" data-testid="situations-horizontal">
        <div ref={trackRef} className="flex h-full w-max will-change-transform">
          {s.scenes.map((scene, i) => (
            <Scene key={scene.n} scene={scene} index={i} total={s.scenes.length} />
          ))}
        </div>
        <div className="absolute bottom-8 left-10 right-10 h-px bg-white/15">
          <motion.div className="h-full origin-left bg-[var(--pz-green)]" style={{ scaleX: progress }} data-testid="situations-progress" />
        </div>
      </div>

      {/* mobile: native snap swipe */}
      <div ref={mWrapRef} className="overflow-hidden border-y hairline py-10 lg:hidden" data-testid="situations-mobile">
        <div ref={mTrackRef} className="no-scrollbar flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 will-change-transform">
          {s.scenes.map((scene, i) => (
            <article
              key={scene.n}
              data-testid={`situation-scene-mobile-${i + 1}`}
              className="flex min-h-[62vh] w-[82vw] shrink-0 snap-center flex-col justify-center border hairline p-6"
            >
              <p className="font-mono-label text-white/50">
                {scene.n} / {String(s.scenes.length).padStart(2, '0')}
              </p>
              <p className="font-display mt-8 text-[10.5vw] leading-[0.9] md:text-[7vw]">
                {scene.lines.map((l, j) => (
                  <span className="block" key={j}>
                    {l}
                  </span>
                ))}
                {scene.extra &&
                  scene.extra.map((l, j) => (
                    <span className="block text-[7.5vw] text-stroke md:text-[5vw]" key={`x${j}`}>
                      {l}
                    </span>
                  ))}
              </p>
              <p className="font-mono-label mt-8 text-white/55">{scene.tags.join(' · ')}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
